import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type Id = string | number;

export interface HorarioMedico {
  id: Id;
  id_medico: number;
  id_especialidad?: Id | null;
  tipo_horario: 'RECURRENTE' | 'FECHA_ESPECIFICA';
  dia_semana: number | null;
  fecha_especifica: string | null;
  activo: boolean;
}

export interface HorarioMedicoCreate {
  id_medico: number;
  id_especialidad?: Id | null;
  tipo_horario: 'RECURRENTE' | 'FECHA_ESPECIFICA';
  dia_semana: number | null;
  fecha_especifica: string | null;
  activo: boolean;
}

export interface TurnoHorario {
  id: Id;
  id_horario: number;
  hora_inicio: string;
  hora_fin: string;
  orden: number;
}

export interface TurnoHorarioCreate {
  id_horario: number;
  hora_inicio: string;
  hora_fin: string;
  orden: number;
}

export interface Medico {
  id: Id;
  id_usuario: number;
  tipo_vinculacion: string;
  matricula: string;
  fecha_registro: string;
  activo: boolean;
}

export interface Usuario {
  id: Id;
  tipo_usuario: string;
  id_empleado: number | null;
  usuario?: string;
  username?: string;
  estado: string;
  estado_log: boolean;
  nombres?: string | null;
  apellidos?: string | null;
  nombre_completo?: string | null;
  email?: string | null;
  activo?: boolean | null;
  roles?: Array<{ codigo?: string | null; nombre?: string | null }>;
}

export interface Empleado {
  id: Id;
  nombres: string;
  apellidos: string;
  cargo?: string;
  estado: string;
}

export interface Especialidad {
  id: Id;
  nombre: string;
  descripcion?: string;
  tipo_especialidad?: string;
  modalidad_atencion?: string;
  duracion_minutos?: number | null;
  activo?: boolean;
}

export interface MedicoEspecialidad {
  id?: Id;
  id_medico: Id;
  id_especialidad: Id;
}

@Injectable({ providedIn: 'root' })
export class HorariosApiService {
  private readonly apiBaseUrl = this.normalizeBaseUrl(environment.apiEndpoint);
  private readonly horariosUrl = `${this.apiBaseUrl}horarios-medico`;
  private readonly turnosUrl = `${this.apiBaseUrl}turnos-horario`;
  private readonly medicosUrl = `${this.apiBaseUrl}medicos`;
  private readonly usuariosUrl = `${this.apiBaseUrl}usuarios`;
  private readonly especialidadesUrl = `${this.apiBaseUrl}especialidades`;
  private readonly medicoEspecialidadUrl = `${this.apiBaseUrl}medico-especialidad`;

  constructor(private http: HttpClient) {}

  private normalizeBaseUrl(value: string | null | undefined): string {
    const raw = String(value ?? '').trim();
    if (!raw) return '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  listarHorarios(params?: { id_medico?: number; activo?: 'true' | 'false' | 'all' }): Observable<HorarioMedico[]> {
    let httpParams = new HttpParams();
    if (params?.id_medico != null) httpParams = httpParams.set('id_medico', String(params.id_medico));
    if (params?.activo && params.activo !== 'all') httpParams = httpParams.set('activo', params.activo);
    httpParams = httpParams.set('_sort', 'id_medico').set('_order', 'asc');

    return this.http.get<HorarioMedico[]>(this.horariosUrl, { params: httpParams }).pipe(
      map((list) => {
        const f = params?.activo;
        if (!f || f === 'all') return list;
        const want = f === 'true';
        return (list ?? []).filter((x) => Boolean((x as any).activo) === want);
      })
    );
  }

  obtenerHorario(id: Id): Observable<HorarioMedico> {
    return this.http.get<HorarioMedico>(`${this.horariosUrl}/${id}`);
  }

  crearHorario(payload: HorarioMedicoCreate): Observable<HorarioMedico> {
    return this.http.post<HorarioMedico>(this.horariosUrl, payload);
  }

  editarHorario(id: Id, patch: Partial<HorarioMedicoCreate>): Observable<HorarioMedico> {
    return this.http.patch<HorarioMedico>(`${this.horariosUrl}/${id}`, patch);
  }

  eliminarHorario(id: Id): Observable<void> {
    return this.http.delete<void>(`${this.horariosUrl}/${id}`);
  }

  listarTurnosPorHorario(id_horario: number): Observable<TurnoHorario[]> {
    let params = new HttpParams();
    params = params.set('id_horario', String(id_horario)).set('_sort', 'orden').set('_order', 'asc');
    return this.http.get<TurnoHorario[]>(this.turnosUrl, { params });
  }

  listarTodosTurnos(): Observable<TurnoHorario[]> {
    let params = new HttpParams();
    params = params.set('_sort', 'id_horario').set('_order', 'asc');
    return this.http.get<TurnoHorario[]>(this.turnosUrl, { params });
  }

  crearTurno(payload: TurnoHorarioCreate): Observable<TurnoHorario> {
    return this.http.post<TurnoHorario>(this.turnosUrl, payload);
  }

  eliminarTurno(id: Id): Observable<void> {
    return this.http.delete<void>(`${this.turnosUrl}/${id}`);
  }

  reemplazarTurnos(id_horario: number, turnos: Array<{ hora_inicio: string; hora_fin: string }>): Observable<void> {
    return this.listarTurnosPorHorario(id_horario).pipe(
      switchMap((existentes) => {
        const deletes = (existentes || []).map((t) => this.eliminarTurno((t as any).id));
        return deletes.length ? forkJoin(deletes) : of([]);
      }),
      switchMap(() => {
        const creates = (turnos || []).map((t, idx) =>
          this.crearTurno({
            id_horario,
            hora_inicio: t.hora_inicio,
            hora_fin: t.hora_fin,
            orden: idx + 1,
          })
        );
        return creates.length ? forkJoin(creates) : of([]);
      }),
      map(() => void 0)
    );
  }

  listarMedicos(): Observable<Medico[]> {
    let params = new HttpParams();
    params = params.set('_sort', 'id').set('_order', 'asc');
    return this.http.get<Medico[]>(this.medicosUrl, { params });
  }

  listarUsuarios(): Observable<Usuario[]> {
    let params = new HttpParams();
    params = params.set('activo', 'true');
    return this.http
      .get<Usuario[]>(this.usuariosUrl, { params })
      .pipe(map((rows) => (rows ?? []).map((row) => this.normalizeUsuario(row))));
  }

  listarEmpleados(): Observable<Empleado[]> {
    return this.listarUsuarios().pipe(map((usuarios) => usuarios.map((usuario) => this.usuarioAsEmpleado(usuario))));
  }

  listarEspecialidades(): Observable<Especialidad[]> {
    let params = new HttpParams();
    params = params.set('_sort', 'nombre').set('_order', 'asc');
    return this.http.get<Especialidad[]>(this.especialidadesUrl, { params }).pipe(
      map((items) => (items || []).filter((x) => (x as any)?.activo !== false)),
    );
  }

  listarMedicoEspecialidad(params?: { id_medico?: Id }): Observable<MedicoEspecialidad[]> {
    let httpParams = new HttpParams();
    if (params?.id_medico != null) httpParams = httpParams.set('id_medico', String(params.id_medico));
    return this.http.get<MedicoEspecialidad[]>(this.medicoEspecialidadUrl, { params: httpParams });
  }

  private normalizeUsuario(row: Usuario | any): Usuario {
    const roles = Array.isArray(row?.roles) ? row.roles : [];
    const nombreCompleto = String(
      row?.nombre_completo ||
      row?.nombreCompleto ||
      [row?.nombres, row?.apellidos].map((value) => String(value ?? '').trim()).filter(Boolean).join(' ')
    ).trim();
    const activo = row?.activo !== false && row?.estado_log !== false;

    return {
      ...row,
      id: row?.id,
      tipo_usuario: String(row?.tipo_usuario || '').trim() || 'ADMINISTRATIVO',
      id_empleado: String(row?.tipo_usuario || '').trim() === 'MEDICO_EXTERNO'
        ? null
        : row?.id_empleado ?? row?.id ?? null,
      usuario: String(row?.usuario || row?.username || '').trim(),
      username: String(row?.username || row?.usuario || '').trim(),
      estado: String(row?.estado ?? '').trim() || (activo ? 'ACTIVO' : 'INACTIVO'),
      estado_log: activo,
      nombres: String(row?.nombres || nombreCompleto || row?.usuario || '').trim() || null,
      apellidos: String(row?.apellidos ?? '').trim() || null,
      nombre_completo: nombreCompleto || null,
      email: String(row?.email ?? '').trim() || null,
      activo,
      roles,
    };
  }

  private usuarioAsEmpleado(usuario: Usuario): Empleado {
    const nombreCompleto = String(usuario.nombre_completo || usuario.nombres || usuario.usuario || '').trim();
    return {
      id: usuario.id,
      nombres: nombreCompleto || `Usuario #${usuario.id}`,
      apellidos: '',
      cargo: usuario.tipo_usuario === 'MEDICO_INTERNO' ? 'MÉDICO INTERNO' : '',
      estado: usuario.estado || (usuario.estado_log === false ? 'INACTIVO' : 'ACTIVO'),
    };
  }
}
